import { useEffect, useRef, useState } from 'react'
import { searchCurrencies } from '../api.js'
import { TopSheet } from './TopSheet.jsx'
import { loadRecentCurrencies, pushRecentCurrency } from '../recentCurrencies.js'

const DEBOUNCE_MS = 250

// Small in-process cache to avoid hammering the backend with the same query
// when the picker is reopened. Cleared on full page reload.
const cache = new Map() // q (lowercased) → array of currencies

async function fetchCurrencies(q) {
  const key = q.trim().toLowerCase()
  if (cache.has(key)) return cache.get(key)
  const docs = await searchCurrencies(key, 50)
  cache.set(key, docs ?? [])
  return docs ?? []
}

export function CurrencySelect({
  value,            // ISO code string, e.g. "EUR"
  onChange,         // (code) => void
  searchPlaceholder = 'Search currency…',
  title = 'Currency',
  closeLabel = 'Close',
  className = '',
}) {
  const [open, setOpen]       = useState(false)
  const [query, setQuery]     = useState('')
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState(null) // for trigger display
  const [recentDocs, setRecentDocs]   = useState([])    // top-3 MRU docs
  const inputRef   = useRef(null)
  const debounceId = useRef(null)
  const queryReq   = useRef(0)

  // Fetch initial list when opening (empty query → 50 alphabetical)
  useEffect(() => {
    if (!open) return
    setQuery('')
    setLoading(true)
    const reqId = ++queryReq.current
    fetchCurrencies('').then(docs => {
      if (queryReq.current !== reqId) return
      setItems(docs)
      setLoading(false)
    })
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [open])

  // Hydrate the docs for the top-N recently-used codes when opening.
  useEffect(() => {
    if (!open) return
    const codes = loadRecentCurrencies()
    if (codes.length === 0) { setRecentDocs([]); return }
    Promise.all(codes.map(code =>
      fetchCurrencies(code.toLowerCase()).then(docs => docs.find(d => d.code === code))
    )).then(found => setRecentDocs(found.filter(Boolean)))
  }, [open])

  // Look up the selected doc so we can render the trigger label
  useEffect(() => {
    if (!value) { setSelectedDoc(null); return }
    fetchCurrencies(value.toLowerCase()).then(docs => {
      const found = docs.find(d => d.code === value)
      if (found) setSelectedDoc(found)
    })
  }, [value])

  // Debounced search on query change
  useEffect(() => {
    if (!open) return
    clearTimeout(debounceId.current)
    debounceId.current = setTimeout(async () => {
      setLoading(true)
      const reqId = ++queryReq.current
      const docs = await fetchCurrencies(query)
      if (queryReq.current !== reqId) return
      setItems(docs)
      setLoading(false)
    }, DEBOUNCE_MS)
    return () => clearTimeout(debounceId.current)
  }, [query, open])

  // Outside-click + Esc dismiss handled by BottomSheet.

  function pick(code) {
    pushRecentCurrency(code)
    onChange(code)
    setOpen(false)
  }

  const trigger = selectedDoc
    ? `${selectedDoc.code}`
    : (value || '—')

  return (
    <div className={`cs-wrap ${className}`}>
      <button
        type="button"
        className={`cs-trigger${open ? ' open' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="cs-trigger-code">{trigger}</span>
        {selectedDoc?.symbol_native && (
          <span className="cs-trigger-sym">{selectedDoc.symbol_native}</span>
        )}
        <span className="cs-trigger-caret" aria-hidden="true">▾</span>
      </button>

      <TopSheet open={open} onClose={() => setOpen(false)} className="cs-sheet">
        <div className="cs-header">
          <span className="cs-h-title">{title}</span>
          <button
            type="button"
            className="cs-h-close"
            onClick={() => setOpen(false)}
            aria-label={closeLabel}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <input
          ref={inputRef}
          type="search"
          className="cs-search"
          placeholder={searchPlaceholder}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <div className="cs-list" role="listbox">
          {loading && items.length === 0 && (
            <div className="cs-empty"><div className="loading-spinner" /></div>
          )}
          {!loading && items.length === 0 && (
            <div className="cs-empty">—</div>
          )}
          {query.trim() === '' && recentDocs.length > 0 && (
            <>
              {recentDocs.map(cur => (
                <button
                  key={'recent-' + cur.code}
                  type="button"
                  className={`cs-row${value === cur.code ? ' selected' : ''}`}
                  onClick={() => pick(cur.code)}
                >
                  <span className="cs-row-code">{cur.code}</span>
                  <span className="cs-row-sym">{cur.symbol_native || cur.symbol}</span>
                  <span className="cs-row-name">{cur.name}</span>
                </button>
              ))}
              <div className="cs-separator" aria-hidden="true" />
            </>
          )}
          {items.map(cur => (
            <button
              key={cur.code}
              type="button"
              className={`cs-row${value === cur.code ? ' selected' : ''}`}
              onClick={() => pick(cur.code)}
            >
              <span className="cs-row-code">{cur.code}</span>
              <span className="cs-row-sym">{cur.symbol_native || cur.symbol}</span>
              <span className="cs-row-name">{cur.name}</span>
            </button>
          ))}
        </div>
      </TopSheet>
    </div>
  )
}
